import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import { useImageContext } from "../../../contexts/ImageContext";
import { clearEditorSession } from "../../../state/editorStore";
import "./uploads.css";
import "../../../components/manage/GalleryCardCheckbox.css";
import logger from "../../../utils/logger";
import {
  scaleFitIcon,
  scaleOriginalIcon,
  downloadNavIcon as downloadIcon,
  deleteNavIcon as deleteIcon,
} from "../../../icons/assets";
import {
  getUploads,
  mapUploadItems,
  MY_DESIGNS_PAGE_SIZE,
  fetchUploadImageBlob,
  downloadUploadAttachment,
  deleteCreation,
} from "../../../services/api/editor/background_removal";
import { saveBlobAsFile } from "../../../services/api/editor/export";
import GalleryLoadMore from "../../../components/manage/GalleryLoadMore";
import RoundLoader from "../../../components/ui/RoundLoader";
import FadeInMedia from "../../../components/ui/FadeInMedia";
import { withAuthReadyRequest } from "../../../utils/withAuthReadyRequest";

function uploadRootId(upload) {
  return (
    upload.root_creation_id ??
    upload.creation_id ??
    Number(String(upload.id).replace(/^upload:/, ""))
  );
}

function uploadDownloadFileName(upload) {
  const ext = upload.input_format || "jpg";
  const normalizedExt = ext === "jpeg" ? "jpg" : ext;
  return `upload-${uploadRootId(upload) || "file"}.${normalizedExt}`;
}

function groupUploadsByDate(uploadsList) {
  const grouped = {};
  uploadsList.forEach((upload) => {
    const date = new Date(upload.created_at).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(upload);
  });
  return grouped;
}

function appendGroupedUploads(previousGrouped, newItems) {
  const seen = new Set();
  const flat = [];
  Object.values(previousGrouped)
    .flat()
    .forEach((upload) => {
      if (!seen.has(upload.id)) {
        seen.add(upload.id);
        flat.push(upload);
      }
    });
  newItems.forEach((upload) => {
    if (!seen.has(upload.id)) {
      seen.add(upload.id);
      flat.push(upload);
    }
  });
  return groupUploadsByDate(flat);
}

const IMAGE_LOAD_CONCURRENCY = 4;
let imageLoadActive = 0;
const imageLoadQueue = [];

function runWithImageLoadLimit(task) {
  return new Promise((resolve, reject) => {
    const run = () => {
      imageLoadActive += 1;
      Promise.resolve()
        .then(task)
        .then(resolve, reject)
        .finally(() => {
          imageLoadActive -= 1;
          const next = imageLoadQueue.shift();
          if (next) next();
        });
    };
    if (imageLoadActive < IMAGE_LOAD_CONCURRENCY) {
      run();
    } else {
      imageLoadQueue.push(run);
    }
  });
}

function UploadCardImage({ upload, getToken, scaleMode }) {
  const cardRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [preferBlob, setPreferBlob] = useState(!upload.output_url);
  const [exhausted, setExhausted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    let observer = null;
    const rootId = uploadRootId(upload);

    setExhausted(false);

    if (upload.output_url && !preferBlob) {
      setSrc(upload.output_url);
      return undefined;
    }

    const loadImage = () =>
      runWithImageLoadLimit(async () => {
        const token = await getToken();
        if (!token || cancelled || !rootId) {
          if (!cancelled) setExhausted(true);
          return;
        }

        const blob = await fetchUploadImageBlob(token, rootId);
        if (cancelled) return;
        // Axios may hand back JSON error bodies as blobs
        if (blob && blob.type && blob.type.includes("json")) {
          throw new Error("Upload image API returned JSON error");
        }
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      }).catch((err) => {
        if (!cancelled) {
          logger.error("Failed to load upload card image", err);
          setExhausted(true);
        }
      });

    const node = cardRef.current;
    if (!node) {
      loadImage();
      return () => {
        cancelled = true;
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer?.disconnect();
          loadImage();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [upload, getToken, preferBlob]);

  return (
    <div ref={cardRef} className="upload-card__media">
      <FadeInMedia
        src={src}
        alt="Upload"
        objectFit={scaleMode}
        loaderSize="sm"
        waiting={!exhausted}
        onError={() => {
          if (upload.output_url && !preferBlob) {
            setPreferBlob(true);
            setSrc(null);
            return;
          }
          setExhausted(true);
        }}
      />
    </div>
  );
}

const Uploads = () => {
  const navigate = useNavigate();
  const { clearImage, setNormalEntry } = useImageContext();
  const [uploads, setUploads] = useState({});
  const [selectedUploads, setSelectedUploads] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [sliderValue, setSliderValue] = useState(50);
  const [scaleMode, setScaleMode] = useState("cover");
  const { getToken } = useAuth();

  const imageSize = 200 + sliderValue * 2;

  const fetchUploadsPage = useCallback(
    async (skip, { append = false } = {}) => {
      const data = await withAuthReadyRequest(getToken, (token) =>
        getUploads(token, {
          skip,
          limit: MY_DESIGNS_PAGE_SIZE,
        })
      );
      const items = mapUploadItems(data);

      if (append) {
        setUploads((prev) => appendGroupedUploads(prev, items));
        setLoadedCount((prev) => prev + items.length);
      } else {
        setUploads(groupUploadsByDate(items));
        setLoadedCount(items.length);
      }

      setTotalCount(data.total_count ?? items.length);
      setHasMore(Boolean(data.has_more));
    },
    [getToken]
  );

  const loadUploads = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      await fetchUploadsPage(0, { append: false });
    } catch (error) {
      logger.error("Failed to fetch uploads", error);
      setUploads({});
      setLoadedCount(0);
      setTotalCount(0);
      setHasMore(false);
      setLoadError("Couldn’t load uploads. Please refresh and try again.");
    } finally {
      setIsLoading(false);
    }
  }, [fetchUploadsPage]);

  const loadMoreUploads = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    try {
      setIsLoadingMore(true);
      await fetchUploadsPage(loadedCount, { append: true });
    } catch (error) {
      logger.error("Failed to load more uploads", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [fetchUploadsPage, hasMore, isLoadingMore, loadedCount]);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  const handleUploadSelect = (uploadId, event) => {
    event?.stopPropagation?.();
    const newSelected = new Set(selectedUploads);
    if (newSelected.has(uploadId)) {
      newSelected.delete(uploadId);
    } else {
      newSelected.add(uploadId);
    }
    setSelectedUploads(newSelected);
  };

  const handleSelectDateGroup = (uploadsList, isSelected) => {
    const newSelected = new Set(selectedUploads);
    uploadsList.forEach((upload) => {
      if (isSelected) {
        newSelected.add(upload.id);
      } else {
        newSelected.delete(upload.id);
      }
    });
    setSelectedUploads(newSelected);
  };

  const handleOpenUpload = (upload) => {
    clearEditorSession();
    setNormalEntry();
    clearImage();
    navigate(`/editor?creationId=${uploadRootId(upload)}`, {
      state: { autoRemoveBackground: true, libraryKind: "upload" },
    });
  };

  const handleDownload = async () => {
    if (selectedUploads.size === 0) {
      alert("Please select uploads to download");
      return;
    }
    try {
      const token = await getToken();
      if (!token) return;

      const flat = Object.values(uploads).flat();
      for (const uploadId of selectedUploads) {
        const upload = flat.find((u) => u.id === uploadId);
        if (!upload) continue;
        const blob = await downloadUploadAttachment(token, uploadRootId(upload));
        saveBlobAsFile(blob, uploadDownloadFileName(upload));
      }
    } catch (error) {
      logger.error("Failed to download uploads", error);
      alert("Failed to download uploads. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (selectedUploads.size === 0) {
      alert("Please select uploads to delete");
      return;
    }

    if (!window.confirm("Are you sure you want to delete the selected uploads?")) {
      return;
    }

    try {
      const token = await getToken();
      if (!token) return;

      const flat = Object.values(uploads).flat();
      for (const uploadId of selectedUploads) {
        const upload = flat.find((u) => u.id === uploadId);
        if (!upload) continue;
        await deleteCreation(token, uploadRootId(upload));
      }

      setSelectedUploads(new Set());
      await loadUploads();
    } catch (error) {
      logger.error("Failed to delete uploads", error);
      alert("Failed to delete uploads. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="uploads-wrapper">
        <div className="uploads-loading">
          <RoundLoader size="lg" label="Loading uploads…" />
        </div>
      </div>
    );
  }

  const hasUploads = Object.keys(uploads).length > 0;

  return (
    <div className="uploads-wrapper">
      <div className="manage-assets-header">
        <h1>Uploads</h1>
        <div className="header-controls" title="Controls">
          <button
            className="control-icon scale-toggle-btn"
            title={scaleMode === "contain" ? "Scale to Fit" : "Scale Original"}
            onClick={() =>
              setScaleMode(scaleMode === "contain" ? "cover" : "contain")
            }
          >
            <img
              src={scaleMode === "contain" ? scaleFitIcon : scaleOriginalIcon}
              alt={scaleMode === "contain" ? "Fit" : "Original"}
              style={{ width: "30px", height: "30px" }}
            />
          </button>
          <div className="slider-container" title="Image Size Controller">
            <input
              type="range"
              min="0"
              max="100"
              value={sliderValue}
              onChange={(e) => setSliderValue(Number(e.target.value))}
              className="volume-slider"
              style={{
                background: `linear-gradient(to right, #0066ff 0%, #0066ff ${sliderValue}%, #333 ${sliderValue}%, #333 100%)`,
              }}
            />
          </div>
        </div>
      </div>

      <div className="manage-assets-main">
        {hasUploads ? (
          Object.entries(uploads).map(([date, uploadsList]) => (
            <div key={date} className="designs-section">
              <div className="designs-date-header">
                <input
                  type="checkbox"
                  className="date-checkbox"
                  onChange={(e) =>
                    handleSelectDateGroup(uploadsList, e.target.checked)
                  }
                  checked={uploadsList.every((u) => selectedUploads.has(u.id))}
                />
                <span className="date-label">{date}</span>
              </div>

              <div
                className="designs-grid"
                style={{
                  gridTemplateColumns: `repeat(auto-fill, minmax(${imageSize}px, ${imageSize}px))`,
                }}
              >
                {uploadsList.map((upload) => (
                  <div
                    key={upload.id}
                    className={`design-card ${
                      selectedUploads.has(upload.id) ? "selected" : ""
                    }`}
                    onClick={() => handleOpenUpload(upload)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleOpenUpload(upload);
                      }
                    }}
                  >
                    <UploadCardImage
                      upload={upload}
                      getToken={getToken}
                      scaleMode={scaleMode}
                    />
                    <div className="design-overlay">
                      <input
                        type="checkbox"
                        checked={selectedUploads.has(upload.id)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleUploadSelect(upload.id, e)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="no-designs">
            <p>{loadError || "No uploads yet."}</p>
          </div>
        )}

        {hasUploads && (
          <GalleryLoadMore
            loadedCount={loadedCount}
            totalCount={totalCount}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMoreUploads}
          />
        )}
      </div>

      <div
        className={`manage-assets-actions${
          selectedUploads.size > 0 ? " actions-visible" : ""
        }`}
        style={selectedUploads.size === 0 ? { pointerEvents: "none" } : {}}
      >
        <button className="action-btn download-btn" onClick={handleDownload}>
          <img
            src={downloadIcon}
            alt="Download"
            className="action-btn-icon action-btn-icon--download"
            aria-hidden="true"
          />
          Download
        </button>
        <button className="action-btn manage-delete-btn" onClick={handleDelete}>
          <img
            src={deleteIcon}
            alt="Delete"
            className="action-btn-icon action-btn-icon--delete"
            aria-hidden="true"
          />
          Delete
        </button>
      </div>
    </div>
  );
};

export default Uploads;
